import React from "react";
import { Shield, Menu, LogOut } from "lucide-react";
import { formatDateTimeIST } from "../../utils/dateTime.ts";
import type { WebsiteRecord } from "../../types/telemetry.ts";
import { WebsiteSelectorDropdown } from "../websites/WebsiteSelectorDropdown.tsx";
import { WebsiteBadge } from "../websites/WebsiteBadge.tsx";

interface HeaderProps {
  isSyntheticMode: boolean;
  isDemoMode?: boolean;
  serverTime?: string | null;
  health?: {
    isHealthy: boolean;
    isConnected: boolean;
    status: string;
    database: string;
    isLoading: boolean;
    error: Error | null;
  };
  onOpenMobileNav?: () => void;
  onSwitchToCustomer?: () => void;
  selectedWebsite?: WebsiteRecord | null;
  websites?: WebsiteRecord[];
  onSelectWebsite?: (website: WebsiteRecord) => void;
  onAddWebsite?: () => void;
  customerEmail?: string | null;
  onLogout?: () => void;
}

export const Header: React.FC<HeaderProps> = ({
  isSyntheticMode,
  isDemoMode,
  serverTime,
  health,
  onOpenMobileNav,
  onSwitchToCustomer,
  selectedWebsite,
  websites = [],
  onSelectWebsite,
  onAddWebsite,
  customerEmail,
  onLogout,
}) => {
  return (
    <header className="h-12 border-b border-border bg-panel px-4 flex items-center justify-between select-none">
      {/* Left: Brand mark, breadcrumbs, and data mode / health status */}
      <div className="flex items-center gap-2.5">
        {onOpenMobileNav && (
          <button
            type="button"
            onClick={onOpenMobileNav}
            aria-label="Open navigation menu"
            className="p-1 -ml-1 rounded-md text-foreground-muted hover:text-foreground hover:bg-panel-hover md:hidden transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
          >
            <Menu className="w-5 h-5" />
          </button>
        )}

        <div className="flex items-center justify-center w-6 h-6 rounded bg-brand-subtle text-brand border border-brand-border">
          <Shield className="w-4 h-4" />
        </div>

        {selectedWebsite ? (
          <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-sm">
            <span className="text-foreground-muted font-medium cursor-default">
              AIShield
            </span>
            <span className="text-foreground-dim">/</span>
            <WebsiteSelectorDropdown
              websites={websites}
              selectedWebsite={selectedWebsite}
              onSelectWebsite={onSelectWebsite || (() => {})}
              onAddWebsite={onAddWebsite}
            />
            <div className="hidden sm:block">
              <WebsiteBadge status={selectedWebsite.onboarding_status} />
            </div>
          </nav>
        ) : (
          <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-sm">
            <span className="text-foreground-muted font-medium cursor-default">
              AIShield
            </span>
            <span className="text-foreground-dim">/</span>
            <span className="text-foreground font-medium">
              {isDemoMode ? "ShieldStore Demo" : "aishield-gateway"}
            </span>
            <span className="text-[11px] font-mono px-1.5 py-0.2 rounded bg-panel-subtle text-foreground-muted border border-border">
              WAF
            </span>
          </nav>
        )}

        {/* Data mode & connection status */}
        {isSyntheticMode ? (
          <span
            data-testid="synthetic-mode-badge"
            className="ml-2 text-[11px] font-mono px-2 py-0.5 rounded bg-panel-subtle text-amber-400 border border-amber-500/30"
          >
            Synthetic Mode · ?mode=synthetic
          </span>
        ) : isDemoMode ? (
          <div className="ml-2 flex items-center gap-2">
            <span
              data-testid="demo-mode-badge"
              className="text-[11px] font-mono px-2 py-0.5 rounded bg-panel-subtle text-brand border border-brand-border"
            >
              Demo Mode · ShieldStore
            </span>
            <span
              data-testid="management-health-badge"
              className="flex items-center gap-1.5 text-[11px] font-mono px-2 py-0.5 rounded bg-panel-subtle text-foreground-muted border border-border"
              title="Reports management service and telemetry database connectivity only. Does not infer gateway, CRS, or AI health."
            >
              <span
                className={`w-1.5 h-1.5 rounded-full ${
                  health?.isConnected
                    ? "bg-emerald-500"
                    : health?.isLoading
                    ? "bg-amber-400 animate-pulse"
                    : "bg-verdict-block"
                }`}
              />
              <span>
                {health?.isConnected
                  ? "API & DB connected"
                  : health?.isLoading
                  ? "Connecting..."
                  : "API unreachable"}
              </span>
            </span>
          </div>
        ) : (
          <div className="ml-2 flex items-center gap-2">
            <span
              data-testid="management-health-badge"
              className="flex items-center gap-1.5 text-[11px] font-mono px-2 py-0.5 rounded bg-panel-subtle text-foreground-muted border border-border"
              title="Reports management service and telemetry database connectivity only. Does not infer gateway, CRS, or AI health."
            >
              <span
                className={`w-1.5 h-1.5 rounded-full ${
                  health?.isConnected
                    ? "bg-emerald-500"
                    : health?.isLoading
                    ? "bg-amber-400 animate-pulse"
                    : "bg-verdict-block"
                }`}
              />
              <span>
                {health?.isConnected
                  ? "API & DB connected"
                  : health?.isLoading
                  ? "Connecting..."
                  : "API unreachable"}
              </span>
            </span>
            {serverTime && (
              <span className="text-[11px] font-mono text-foreground-dim hidden md:inline">
                {formatDateTimeIST(serverTime)}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Right: Actions */}
      <div className="flex items-center gap-3">
        {import.meta.env.DEV && onSwitchToCustomer && (
          <button
            type="button"
            onClick={onSwitchToCustomer}
            className="text-xs font-mono px-2.5 py-1 rounded bg-canvas hover:bg-panel-hover border border-border text-foreground-muted hover:text-foreground transition-colors"
          >
            Customer Portal
          </button>
        )}

        {customerEmail && (
          <div className="flex items-center gap-2">
            <span className="text-xs font-mono text-foreground-muted hidden lg:inline max-w-[160px] truncate" title={customerEmail}>
              {customerEmail}
            </span>
            {onLogout && (
              <button
                type="button"
                onClick={onLogout}
                aria-label="Sign out of account"
                title="Sign out of customer account"
                className="flex items-center gap-1.5 text-xs font-mono px-2.5 py-1 rounded bg-canvas hover:bg-panel-hover border border-border text-foreground-muted hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-brand"
              >
                <LogOut className="w-3 h-3" />
                <span className="hidden sm:inline">Sign Out</span>
              </button>
            )}
          </div>
        )}

        <div className="hidden sm:flex items-center gap-2 px-2.5 py-1 rounded-md bg-canvas border border-border text-xs text-foreground-muted">
          <span>Search...</span>
          <kbd className="px-1.5 py-0.5 rounded bg-panel border border-border text-[10px] font-mono text-foreground-dim">
            ⌘K
          </kbd>
        </div>
      </div>
    </header>
  );
};

